import type { ReactNode } from 'react'
import { Plus, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

export interface CredentialFieldRow {
  key: string
  value: string
  // Chave estável de React (cada modal gera o seu id). O index signature
  // satisfaz o FormDataConvertible do useForm do Inertia.
  _id: string
  [key: string]: string
}

interface Props {
  // Prefixo único dos ids dos inputs (um modal pode coexistir com outro no DOM).
  idPrefix: string
  name: string
  onNameChange: (value: string) => void
  nameError?: string
  nameDisabled?: boolean
  fields: CredentialFieldRow[]
  onFieldChange: (index: number, key: 'key' | 'value', value: string) => void
  onAddField: () => void
  onRemoveField: (index: number) => void
  fieldsError?: string
  // Edição: campos já persistidos não podem ter identificador/valor alterados
  // nem ser removidos (só acrescentar novos).
  isFieldDisabled?: (index: number) => boolean
  valuePlaceholder?: (index: number) => string
  // Slot opcional logo abaixo do Nome (a Descrição, na edição).
  afterName?: ReactNode
}

/**
 * Campos compartilhados do formulário de credencial — Nome + pares
 * identificador/valor + "Adicionar campo". É a FONTE ÚNICA do formulário: o
 * "Criar nova" do portal do agente (ConnectCredentialModal), o "Nova credencial"
 * da organização e o passo de credencial das integrações (CredentialFormModal)
 * renderizam exatamente este fieldset — sem duas cópias divergirem como antes.
 */
export function CredentialFieldset({
  idPrefix,
  name,
  onNameChange,
  nameError,
  nameDisabled = false,
  fields,
  onFieldChange,
  onAddField,
  onRemoveField,
  fieldsError,
  isFieldDisabled,
  valuePlaceholder,
  afterName,
}: Props) {
  return (
    <div className="space-y-3">
      <div className="grid gap-2">
        <Label htmlFor={`${idPrefix}-name`}>Nome</Label>
        <Input
          id={`${idPrefix}-name`}
          value={name}
          onChange={(e) => onNameChange(e.target.value)}
          placeholder="Ex.: openai_api_key"
          disabled={nameDisabled}
        />
        {nameError && <p className="text-sm text-destructive" role="alert">{nameError}</p>}
      </div>

      {afterName}

      {fields.map((field, index) => {
        const disabled = isFieldDisabled?.(index) ?? false
        return (
          <div key={field._id} className="flex items-end gap-2">
            <div className="grid flex-1 gap-2">
              {index === 0 && <Label htmlFor={`${idPrefix}-key-${index}`}>Identificador</Label>}
              <Input
                id={`${idPrefix}-key-${index}`}
                value={field.key}
                onChange={(e) => onFieldChange(index, 'key', e.target.value)}
                placeholder="Ex.: api_key"
                disabled={disabled}
              />
            </div>
            <div className="grid flex-1 gap-2">
              {index === 0 && <Label htmlFor={`${idPrefix}-value-${index}`}>Valor</Label>}
              <Input
                id={`${idPrefix}-value-${index}`}
                type="password"
                value={field.value}
                onChange={(e) => onFieldChange(index, 'value', e.target.value)}
                placeholder={valuePlaceholder?.(index) ?? 'Cole a chave/segredo'}
              />
            </div>
            <div className="pb-1">
              {fields.length > 1 && !disabled && (
                <Button
                  variant="ghost"
                  size="icon-sm"
                  onClick={() => onRemoveField(index)}
                  aria-label="Remover campo"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
        )
      })}

      <Button variant="ghost" size="sm" onClick={onAddField}>
        <Plus className="h-4 w-4" />
        Adicionar campo
      </Button>

      {fieldsError && <p className="text-sm text-destructive" role="alert">{fieldsError}</p>}
    </div>
  )
}
